import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authAPI } from '../utils/api';

const MobileLoginPage = ({ setUser }) => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();
  }, []);

  const handleCodeChange = (e) => {
    const value = e.target.value;
    // Only allow digits, max 4
    if (/^\d*$/.test(value) && value.length <= 4) {
      setCode(value);
      
      // Auto-submit when 4 digits entered
      if (value.length === 4) {
        handleSubmit(value);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && code.length === 4) {
      handleSubmit(code);
    }
  };

  const handleSubmit = async (fullCode) => {
    const codeStr = fullCode || code;
    if (codeStr.length !== 4) {
      toast.error('Please enter all 4 digits');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.loginCode({ code: codeStr });
      const { token, user, requires_setup } = response.data;

      localStorage.setItem('movemate_token', token);
      localStorage.setItem('movemate_user', JSON.stringify(user));
      setUser(user);

      toast.success(`Welcome ${user.first_name}!`);

      // Route based on role and profile completion
      if (user.role === 'customer') {
        if (!user.profile_completed) {
          navigate('/mobile/setup');
        } else {
          navigate('/mobile/dashboard');
        }
      } else if (requires_setup) {
        navigate('/setup-wizard');
      } else {
        navigate('/mobile/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{
        background: 'linear-gradient(135deg, #1A73E8 0%, #00C4CC 100%)'
      }}
      data-testid="mobile-login-page"
    >
      {/* Logo Section */}
      <div className="flex flex-col items-center mb-12">
        <img 
          src="/logo.png" 
          alt="MoveMate" 
          className="w-32 h-32 mb-6 drop-shadow-2xl"
        />
        <h1 
          className="text-4xl font-bold tracking-wider"
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #e0f7fa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        >
          MOVEMATE
        </h1>
      </div>

      {/* Login Box */}
      <div 
        className="w-full max-w-sm rounded-2xl p-8"
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div className="text-center mb-8">
          <h2 className="text-white text-xl font-semibold mb-2">Enter Your Code</h2>
          <p className="text-slate-400 text-sm">
            Enter the 4-digit code from your company
          </p>
        </div>

        {/* Single Input Box */}
        <div className="mb-6">
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={code}
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            placeholder="• • • •"
            disabled={loading}
            className="w-full h-16 text-center text-3xl font-bold tracking-[0.5em] rounded-xl border-2 border-slate-600 bg-slate-800 text-white placeholder-slate-500 focus:border-[#00C4CC] focus:ring-2 focus:ring-[#00C4CC]/50 focus:outline-none transition-all"
            style={{
              letterSpacing: '0.5em',
              paddingLeft: '0.5em'
            }}
            data-testid="code-input"
          />
        </div>

        {/* Login Button */}
        <button
          onClick={() => handleSubmit(code)}
          disabled={loading || code.length !== 4}
          className="w-full h-14 rounded-xl font-semibold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: '#ffffff',
            color: '#1A73E8'
          }}
          data-testid="submit-code-btn"
        >
          {loading ? 'Verifying...' : 'Login'}
        </button>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 text-sm">
            Don&apos;t have a code?
          </p>
          <p className="text-slate-500 text-xs mt-1">
            Contact your moving company to get access
          </p>
        </div>
      </div>

      {/* Company Admin Link */}
      <div className="mt-8">
        <a 
          href="/login" 
          className="text-white/80 text-sm hover:text-white transition-colors"
        >
          Company admin? <span className="underline">Login here</span>
        </a>
      </div>
    </div>
  );
};

export default MobileLoginPage;
